/**
 * Relewise recommendation rows — fills every [data-relewise-row] on the page (snippets/relewise-row.liquid):
 * product page rows, front page, collection, 404, no search results.
 *
 * All rows that load together are ONE batched request with distinct products across rows (Relewise go-live
 * check 2026-09-15). On a product page every row excludes the product itself, and product-based rows send
 * product id + variant SKU. Cards are the theme's own (sections/relewise-cards.liquid), rendered in
 * the order Relewise returns them, without products Shopify shows as sold out (Relewise counts the physical shop's
 * stock as for sale; a few extra are asked for to keep the row full). A row Relewise has nothing for hides its
 * section ([data-relewise-section]): Relewise stays the source. Only if Relewise fails (not loaded, or the request
 * fails) does the section's baseline (e.g. its collection) show. A slow answer is waited for.
 *
 * Lazy rows ([data-relewise-lazy]; search: popular products) load only when the listing finds nothing
 * (relewise:no-results from assets/relewise-listing.js).
 */
(() => {
  const core = window.agf && window.agf.relewiseCore;
  const cfg = window.agf && window.agf.relewise;
  const rows = [...document.querySelectorAll('[data-relewise-row]')];
  if (!rows.length) return;

  const MINUTES_PER_DAY = 24 * 60;
  const SPARE = 4; // extra recommendations, for cards left out (sold out online, Locksmith)

  function section(row) {
    return row.closest('[data-relewise-section]');
  }

  /** Relewise failed: the section's baseline, if it has one. */
  function fallBack(row) {
    const baseline = row.querySelector('template[data-relewise-baseline]');
    const grid = row.querySelector('[data-grid-slider]');
    if (baseline && grid) {
      grid.innerHTML = baseline.innerHTML;
      reveal(row);
      return;
    }
    hide(row);
  }

  function hide(row) {
    const wrapper = section(row);
    // Theme editor only (the note is rendered in design mode): keep the row's block visible, with its note.
    const note = wrapper && wrapper.querySelector('[data-relewise-editor-note]');
    if (note) {
      note.hidden = false;
      row.hidden = true;
      wrapper.hidden = false;
      return;
    }
    if (wrapper) wrapper.hidden = true;
    else row.hidden = true;
  }

  function reveal(row) {
    const wrapper = section(row);
    if (wrapper) wrapper.hidden = false;
    const slider = row.querySelector('grid-slider');
    if (slider) slider.dispatchEvent(new CustomEvent('theme:grid-slider:init', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('wishlist:toggleButtonState'));
  }

  function popular(settings, days) {
    return new Relewise.PopularProductsBuilder(settings).basedOn('MostPurchased').sinceMinutesAgo(days * MINUTES_PER_DAY);
  }

  function builder(row, user) {
    const { Relewise } = window;
    const settings = core.settings(row.dataset.location, user);
    const days = Number(row.dataset.days) || 14;
    const product = cfg.product && { productId: cfg.product.id, variantId: cfg.product.landingSku || undefined };

    switch (row.dataset.type) {
      case 'personal':
        return Relewise.userIsAnonymous && Relewise.userIsAnonymous(user)
          ? popular(settings, days)
          : new Relewise.PersonalProductRecommendationBuilder(settings);
      case 'recently_viewed':
        return new Relewise.RecentlyViewedProductsBuilder(settings);
      case 'viewed_after':
        return product && new Relewise.ProductsViewedAfterViewingProductBuilder(settings).product(product);
      case 'purchased_with':
        return product && new Relewise.PurchasedWithProductBuilder(settings).product(product);
      case 'popular':
        return popular(settings, days);
      default:
        return null;
    }
  }

  function build(row, user, forceType) {
    if (forceType) row.dataset.type = forceType;
    const b = builder(row, user);
    if (!b) return null;
    return b
      .setNumberOfRecommendations((Number(row.dataset.limit) || 4) + SPARE)
      .setSelectedProductProperties({ dataKeys: [core.HANDLE_KEY] })
      .filters((filters) => {
        core.recommendationFilters(filters);
        if (cfg.product) filters.addProductIdFilter([cfg.product.id], true);
      })
      .build();
  }

  async function render(row, response) {
    const limit = Number(row.dataset.limit) || 4;
    const cards = (await core.cards(core.handles(response && response.recommendations), { sizes: row.dataset.cardSizes, sellable: true })).slice(0, limit);
    const grid = row.querySelector('[data-grid-slider]');
    if (!cards.length || !grid) return false;

    const host = row.closest('[id]');
    const anchor = host ? `#${host.id}` : '';
    grid.innerHTML = cards.map((card, i) => core.fillTokens(card.html, { anchor, delay: i * 100 })).join('');
    // Injected after the page's scroll animations ran: show them straight away.
    grid.querySelectorAll('[data-aos]').forEach((el) => el.classList.add('aos-animate'));
    reveal(row);
    return true;
  }

  async function run(rows) {
    if (!rows.length) return;
    if (!core || !core.ready()) {
      rows.forEach(fallBack);
      return;
    }
    let user;
    try {
      user = await core.user();
    } catch (error) {
      rows.forEach(fallBack);
      return;
    }

    // Queue every row in the same tick → one batched request.
    const pending = rows.map((row) => {
      const request = build(row, user);
      return { row, promise: request ? core.recommend(request) : Promise.resolve(null) };
    });

    const retry = [];
    await Promise.all(pending.map(async ({ row, promise }) => {
      let response;
      try {
        response = await promise;
      } catch (error) {
        fallBack(row);
        return;
      }
      if (await render(row, response).catch(() => false)) return;
      // A visitor without personal history gets popular products instead (second, smaller batch).
      if (row.dataset.type === 'personal') retry.push(row);
      else hide(row);
    }));

    await Promise.all(retry.map(async (row) => {
      let response;
      try {
        response = await core.recommend(build(row, user, 'popular'));
      } catch (error) {
        fallBack(row);
        return;
      }
      if (!(await render(row, response).catch(() => false))) hide(row);
    }));

    // A section holding several rows (product page) disappears when none of them has products.
    document.querySelectorAll('[data-relewise-group]').forEach((group) => {
      group.hidden = ![...group.querySelectorAll('[data-relewise-section]')].some((wrapper) => !wrapper.hidden);
    });
  }

  const isLazy = (row) => row.hasAttribute('data-relewise-lazy');
  run(rows.filter((row) => !isLazy(row)));

  const lazyRows = rows.filter(isLazy);
  if (lazyRows.length) {
    document.addEventListener('relewise:no-results', () => run(lazyRows), { once: true });
  }
})();
